import {
  type TboCityListQuery,
  type TboCityListResponse,
  TboCityListResponseSchema,
  type TboCityOption,
  TboCityOptionListSchema,
  type TboCountryListQuery,
  type TboCountryListResponse,
  TboCountryListResponseSchema,
  type TboHotelListQuery,
  type TboHotelListResponse,
  TboHotelListResponseSchema,
  type TboHotelSyncRequestInput,
  type TboHotelSyncResponse,
  TboHotelSyncResponseSchema,
  type TboHotelSyncStatus,
  TboHotelSyncStatusSchema,
  type TboStateListQuery,
  type TboStateListResponse,
  TboStateListResponseSchema,
  type TboSyncResponse,
  TboSyncResponseSchema,
  type TboSyncStatus,
  TboSyncStatusSchema,
} from '@utazzo/shared';

import {
  buildTboHotelSyncCheckpoint,
  type TboHotelSyncCheckpoint,
} from './tbo-hotel-sync-checkpoint';

import { apiClient } from '@/lib/api-client';

/** TBO geography reference API (read-only) + admin sync. */

export const listTboCountries = async (
  query: TboCountryListQuery,
): Promise<TboCountryListResponse> => {
  const { data } = await apiClient.get<unknown>('/tbo/countries', { params: query });
  return TboCountryListResponseSchema.parse(data);
};

export const listTboStates = async (query: TboStateListQuery): Promise<TboStateListResponse> => {
  const { data } = await apiClient.get<unknown>('/tbo/states', { params: query });
  return TboStateListResponseSchema.parse(data);
};

export const listTboCities = async (query: TboCityListQuery): Promise<TboCityListResponse> => {
  const { data } = await apiClient.get<unknown>('/tbo/cities', { params: query });
  return TboCityListResponseSchema.parse(data);
};

export const listTboCityOptions = async (
  countryCode: string,
  q?: string,
): Promise<TboCityOption[]> => {
  const { data } = await apiClient.get<unknown>('/tbo/cities/options', {
    params: { countryCode, ...(q ? { q } : {}) },
  });
  return TboCityOptionListSchema.parse(data);
};

export const syncTbo = async (): Promise<TboSyncResponse> => {
  const { data } = await apiClient.post<unknown>('/tbo/sync');
  return TboSyncResponseSchema.parse(data);
};

export const getTboStatus = async (): Promise<TboSyncStatus> => {
  const { data } = await apiClient.get<unknown>('/tbo/status');
  return TboSyncStatusSchema.parse(data);
};

/**
 * 2026-07-06 ADR-0059. TBO hotel mirror admin. Backed by the
 * `tbo_hotels` table (populated by the on-search lazy backfill or
 * the batch sync). The `Hotels` tab on the TBO Geography page reads
 * from these + fires the sync.
 */
export const listTboHotels = async (query: TboHotelListQuery): Promise<TboHotelListResponse> => {
  const { data } = await apiClient.get<unknown>('/tbo-hotel', { params: query });
  return TboHotelListResponseSchema.parse(data);
};

/** Batch hotel sync runs in chunks (default 25 hotels/request) to stay within proxy timeouts. */
export type TboHotelSyncProgress = {
  processedInCity: number;
  totalInCity: number;
  currentCityId: string | null;
};

export type TboHotelSyncTotals = {
  upserted: number;
  errors: number;
  processedTotal: number;
};

export type RunTboHotelSyncOptions = {
  signal?: AbortSignal;
  initialTotals?: TboHotelSyncTotals;
  onProgress?: (progress: TboHotelSyncProgress, totals: TboHotelSyncTotals) => void;
  onCheckpoint?: (checkpoint: TboHotelSyncCheckpoint) => void;
};

export const runTboHotelSync = async (
  input: TboHotelSyncRequestInput,
  options: RunTboHotelSyncOptions = {},
): Promise<TboHotelSyncResponse> => {
  let cityIndex = Number(input.cityIndex ?? 0);
  let offset = Number(input.offset ?? 0);
  let upserted = options.initialTotals?.upserted ?? 0;
  let errors = options.initialTotals?.errors ?? 0;
  let processedTotal = options.initialTotals?.processedTotal ?? 0;
  let last: TboHotelSyncResponse | undefined;

  while (true) {
    if (options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const { data } = await apiClient.post<unknown>(
      '/tbo-hotel/sync',
      {
        ...input,
        cityIndex,
        offset,
      },
      { signal: options.signal },
    );
    const chunk = TboHotelSyncResponseSchema.parse(data);
    last = chunk;
    upserted += chunk.upserted;
    errors += chunk.errors;
    processedTotal += chunk.processed;

    const totals: TboHotelSyncTotals = { upserted, errors, processedTotal };
    options.onProgress?.(
      {
        processedInCity: chunk.nextOffset ?? chunk.hotels,
        totalInCity: chunk.hotels,
        currentCityId: chunk.currentCityId,
      },
      totals,
    );

    let nextCityIndex = cityIndex;
    let nextOffset = offset;
    if (!chunk.done) {
      if (chunk.nextOffset !== null && chunk.nextOffset !== undefined) {
        nextOffset = Number(chunk.nextOffset);
      } else if (chunk.nextCityIndex !== null && chunk.nextCityIndex !== undefined) {
        nextCityIndex = Number(chunk.nextCityIndex);
        nextOffset = 0;
      }
    }

    if (!chunk.done) {
      options.onCheckpoint?.(
        buildTboHotelSyncCheckpoint(
          input,
          { cityIndex: nextCityIndex, offset: nextOffset },
          totals,
          { lastCityId: chunk.currentCityId, totalInCity: chunk.hotels },
        ),
      );
    }

    if (chunk.done) break;

    if (chunk.nextOffset !== null && chunk.nextOffset !== undefined) {
      offset = Number(chunk.nextOffset);
    } else if (chunk.nextCityIndex !== null && chunk.nextCityIndex !== undefined) {
      cityIndex = Number(chunk.nextCityIndex);
      offset = 0;
    } else {
      break;
    }
  }

  if (!last) {
    throw new Error('TBO hotel sync returned no chunks');
  }

  return {
    ...last,
    upserted,
    errors,
    processed: processedTotal,
    hotels: processedTotal,
    done: true,
    nextOffset: null,
    nextCityIndex: null,
  };
};

/** @deprecated Prefer `runTboHotelSync` with options for cancel/resume. */
export const syncTboHotels = async (
  input: TboHotelSyncRequestInput,
  onProgress?: (progress: TboHotelSyncProgress) => void,
): Promise<TboHotelSyncResponse> =>
  runTboHotelSync(input, {
    onProgress: onProgress
      ? (progress, _totals) => {
          onProgress(progress);
        }
      : undefined,
  });

export const getTboHotelStatus = async (): Promise<TboHotelSyncStatus> => {
  const { data } = await apiClient.get<unknown>('/tbo-hotel/status');
  return TboHotelSyncStatusSchema.parse(data);
};
